import React, { useState, useEffect, useRef } from "react";
import { Row, Col, Card, Form, Button, Spinner, Alert, Modal } from "react-bootstrap";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import SunEditor from "suneditor-react";
import "suneditor/dist/css/suneditor.min.css";
import { FiPlus, FiX } from "react-icons/fi";
import PageLayout from "../../layouts/PageLayout";

export default function SellerAddProductPage() {
    const navigate = useNavigate();

    const [loadingInit, setLoadingInit] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Initial Data States
    const [brands, setBrands] = useState([]);
    const [categories, setCategories] = useState([]);
    const [subCategories, setSubCategories] = useState([]);
    const [selectedParentCategory, setSelectedParentCategory] = useState("");
    const [attributeValues, setAttributeValues] = useState([]);
    const [activeAttributeSuggestion, setActiveAttributeSuggestion] = useState(null);
    const [loadingAttributes, setLoadingAttributes] = useState(false);
    const [categoryAttributesLoaded, setCategoryAttributesLoaded] = useState(false);
    const [showListingDetails, setShowListingDetails] = useState(false);
    const [preparingListingDetails, setPreparingListingDetails] = useState(false);

    // Form State aligned with storeFreshProduct requirements
    const [formData, setFormData] = useState({
        category_id: "",
        brand_id: "",
        title: "",
        product_condition: "new",
        selling_style: "Fixed",
        auction_min_price: "",
        buying_price: "",
        sell_price_dollar: "",
        shipping_charges_dollar: "",
        short_buying_price: "",
        product_qty: 1,
        short_description: "",
        description: "",
    });

    const [mainImage, setMainImage] = useState(null);
    const [mainImagePreview, setMainImagePreview] = useState(null);
    const [galleryFiles, setGalleryFiles] = useState([]);
    const [galleryPreviews, setGalleryPreviews] = useState([]);
    const previewUrls = useRef(new Set());
    const [brandModalOpen, setBrandModalOpen] = useState(false);
    const [brandName, setBrandName] = useState("");
    const [addingBrand, setAddingBrand] = useState(false);

    const getAuthHeaders = () => ({
        Authorization: `Bearer ${localStorage.getItem("authToken") || localStorage.getItem("AuthUser")}`,
    });

    // Fetch Initial Brands & Categories
    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const res = await axios.get(
                    `${process.env.REACT_APP_API_URL}/store-seller/products/initial-data`,
                    { headers: getAuthHeaders() }
                );
                setBrands(res.data?.brand || []);
                setCategories(res.data?.product_category || []);
            } catch (err) {
                console.error("Fetch initial data error:", err);
                setError("Failed to load categories and brands.");
            } finally {
                setLoadingInit(false);
            }
        };

        fetchInitialData();
    }, []);

    // Parent Category Selection Logic
    const handleParentCategoryChange = (e) => {
        const parentId = e.target.value;
        setSelectedParentCategory(parentId);
        setFormData((prev) => ({ ...prev, category_id: "" }));
        setAttributeValues([]);
        setCategoryAttributesLoaded(false);
        setShowListingDetails(false);
        setPreparingListingDetails(false);

        const selectedCat = categories.find((c) => String(c.id) === String(parentId));
        if (selectedCat && selectedCat.child_categories) {
            setSubCategories(selectedCat.child_categories);
        } else {
            setSubCategories([]);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === "category_id") {
            setAttributeValues([]);
            setCategoryAttributesLoaded(false);
            setShowListingDetails(false);
            setPreparingListingDetails(false);
        }
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    useEffect(() => {
        if (!formData.category_id) {
            setAttributeValues([]);
            setLoadingAttributes(false);
            setCategoryAttributesLoaded(false);
            return undefined;
        }

        let isCurrentCategory = true;
        const fetchCategoryAttributes = async () => {
            setLoadingAttributes(true);
            const loadingDelay = new Promise((resolve) => setTimeout(resolve, 500));
            try {
                const response = await axios.get(
                    `${process.env.REACT_APP_API_URL}/categories/${formData.category_id}/attributes`,
                    { headers: getAuthHeaders() }
                );
                await loadingDelay;
                if (!isCurrentCategory) return;

                const attributes = Array.isArray(response.data?.attributes)
                    ? response.data.attributes
                        .map((attribute) => ({
                            name: typeof attribute === "string" ? attribute : attribute?.name,
                            suggestions: Array.isArray(attribute?.values)
                                ? attribute.values.map(String)
                                : [],
                        }))
                        .filter((attribute) => typeof attribute.name === "string" && attribute.name.trim())
                    : [];
                setAttributeValues(attributes.map(({ name, suggestions }) => ({ name, value: "", suggestions })));
            } catch (requestError) {
                await loadingDelay;
                if (!isCurrentCategory) return;
                if (requestError.response?.status === 404) {
                    setAttributeValues([]);
                } else {
                    console.error("Failed to fetch category attributes:", requestError);
                    setError(requestError.response?.data?.message || "Failed to load category attributes.");
                }
            } finally {
                if (isCurrentCategory) {
                    setLoadingAttributes(false);
                    setCategoryAttributesLoaded(true);
                }
            }
        };

        fetchCategoryAttributes();
        return () => {
            isCurrentCategory = false;
        };
    }, [formData.category_id]);

    const initialListingDetailsComplete = Boolean(
        mainImage &&
        formData.title.trim() &&
        selectedParentCategory &&
        formData.category_id
    );

    useEffect(() => {
        if (!initialListingDetailsComplete) {
            setShowListingDetails(false);
            setPreparingListingDetails(false);
            return undefined;
        }

        if (!categoryAttributesLoaded) return undefined;

        setPreparingListingDetails(true);
        const transition = setTimeout(() => {
            setPreparingListingDetails(false);
            setShowListingDetails(true);
        }, 700);

        return () => clearTimeout(transition);
    }, [initialListingDetailsComplete, categoryAttributesLoaded]);

    const handleAttributeChange = (indexToUpdate, value) => {
        setAttributeValues((current) => current.map((attribute, index) => (
            index === indexToUpdate ? { ...attribute, value } : attribute
        )));
    };

    const handleAttributeNameChange = (indexToUpdate, name) => {
        setAttributeValues((current) => current.map((attribute, index) => (
            index === indexToUpdate ? { ...attribute, name } : attribute
        )));
    };

    const addAttribute = () => {
        setAttributeValues((current) => [...current, { name: "", value: "", suggestions: [], isCustom: true }]);
    };

    const removeAttribute = (indexToRemove) => {
        setAttributeValues((current) => current.filter((_, index) => index !== indexToRemove));
    };

    const handleShortDescriptionChange = (value) => {
        setFormData((prev) => ({ ...prev, short_description: value }));
    };

    const handleDescriptionChange = (value) => {
        setFormData((prev) => ({ ...prev, description: value }));
    };

    const handleAddBrand = async (event) => {
        event.preventDefault();
        setAddingBrand(true);
        setError(null);
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/store-seller/brand`,
                { name: brandName.trim() },
                { headers: getAuthHeaders() }
            );
            const brand = response.data?.brand;
            if (brand) {
                setBrands((current) => [brand, ...current]);
                setFormData((current) => ({ ...current, brand_id: String(brand.id) }));
            }
            setBrandName("");
            setBrandModalOpen(false);
        } catch (err) {
            setError(err.response?.data?.message || "Could not add brand.");
        } finally {
            setAddingBrand(false);
        }
    };

    const handleMainImageChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (mainImagePreview) {
            URL.revokeObjectURL(mainImagePreview);
            previewUrls.current.delete(mainImagePreview);
        }
        const preview = URL.createObjectURL(file);
        previewUrls.current.add(preview);
        setMainImage(file);
        setMainImagePreview(preview);
    };

    const handleGalleryImagesChange = (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = "";
        if (!files.length) return;

        setGalleryFiles((prev) => [...prev, ...files]);
        const newPreviews = files.map((file) => {
            const preview = URL.createObjectURL(file);
            previewUrls.current.add(preview);
            return preview;
        });
        setGalleryPreviews((prev) => [...prev, ...newPreviews]);
    };

    const removeGalleryImage = (index) => {
        URL.revokeObjectURL(galleryPreviews[index]);
        previewUrls.current.delete(galleryPreviews[index]);
        setGalleryFiles((prev) => prev.filter((_, i) => i !== index));
        setGalleryPreviews((prev) => prev.filter((_, i) => i !== index));
    };

    useEffect(() => () => {
        previewUrls.current.forEach((preview) => URL.revokeObjectURL(preview));
        previewUrls.current.clear();
    }, []);

    const editorButtonList = [
        ["undo", "redo"],
        ["font", "fontSize", "formatBlock"],
        ["bold", "underline", "italic", "strike"],
        ["removeFormat"],
        ["outdent", "indent"],
        ["align", "horizontalRule", "list"],
        ["table", "link", "image"],
        ["fullScreen", "codeView"],
    ];

    const validateForm = () => {
        if (!formData.title.trim()) {
            setError("Product title is required.");
            return false;
        }
        if (!formData.category_id) {
            setError("Please select a sub category.");
            return false;
        }
        if (!formData.brand_id) {
            setError("Please select a brand.");
            return false;
        }
        if (formData.buying_price === "" || Number(formData.buying_price) < 0) {
            setError("Please enter a valid Buying Price.");
            return false;
        }
        if (formData.sell_price_dollar === "" || Number(formData.sell_price_dollar) < 0) {
            setError("Please enter sell price in dollar.");
            return false;
        }
        if (formData.shipping_charges_dollar === "" || Number(formData.shipping_charges_dollar) < 0) {
            setError("Please enter shipping charges in dollar.");
            return false;
        }
        if (formData.buying_price === "" || Number(formData.buying_price) < 0) {
            setError("Please enter buying price.");
            return false;
        }
        if (formData.short_buying_price === "" || Number(formData.short_buying_price) < 0) {
            setError("Please enter short buying price.");
            return false;
        }

        const hasEditorText = (value) =>
            value.replace(/<[^>]*>/g, "").replace(/&nbsp;|&#160;/g, " ").trim().length > 0;

        if (!hasEditorText(formData.short_description)) {
            setError("Short description is required.");
            return false;
        }
        if (!hasEditorText(formData.description)) {
            setError("Full description is required.");
            return false;
        }
        if (!mainImage) {
            setError("Main product card image (card_img) is required.");
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);

        if (!validateForm()) return;
        setSaving(true);

        try {
            const submitData = new FormData();
            Object.keys(formData).forEach((key) => {
                if (key === "auction_min_price" && formData.selling_style !== "Auction") {
                    return;
                }
                if (formData[key] !== null && formData[key] !== undefined && formData[key] !== "") {
                    submitData.append(key, formData[key]);
                }
            });
            attributeValues
                .filter((attribute) => attribute.name.trim())
                .forEach((attribute, index) => {
                    submitData.append(`attributes[${index}][name]`, attribute.name);
                    submitData.append(`attributes[${index}][value]`, attribute.value);
                });

            if (mainImage) {
                submitData.append("card_img", mainImage);
            }

            galleryFiles.forEach((file, index) => {
                submitData.append(`images[${index}]`, file);
            });

            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/store-seller/products`,
                submitData,
                {
                    headers: {
                        ...getAuthHeaders(),
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            setSuccess("Fresh product created successfully!");
            setTimeout(() => navigate("/seller-products"), 1200);
        } catch (err) {
            console.error("Create fresh product error:", err);
            if (err.response?.data?.errors) {
                const messages = Object.values(err.response.data.errors).flat().join(" ");
                setError(messages);
            } else {
                setError(err.response?.data?.message || "Failed to create product.");
            }
        } finally {
            setSaving(false);
        }
    };

    if (loadingInit) {
        return (
            <PageLayout>
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2">Loading Categories & Brands...</p>
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card mb-4">
                        <div className="mc-breadcrumb">
                            <h3 className="mc-breadcrumb-title">Add Fresh Product</h3>
                            <ul className="mc-breadcrumb-list">
                                <li className="mc-breadcrumb-item"><Link to="#">Home</Link></li>
                                <li className="mc-breadcrumb-item">Add Fresh Product</li>
                            </ul>
                        </div>
                    </div>
                </Col>

                <Col xl={12}>
                    {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
                    {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}
                </Col>

                <Col xl={12}>
                    <Form onSubmit={handleSubmit}>
                        {/* eBay-style listing start: image, title and category */}
                        <Card className="mb-4 shadow-sm">
                            <Card.Header className="py-3">
                                <div className="d-flex flex-wrap align-items-center gap-3">
                                    <span className="d-inline-flex align-items-center gap-2 fw-semibold text-primary">
                                        <span className="rounded-circle bg-primary text-white d-inline-flex align-items-center justify-content-center" style={{ width: 28, height: 28 }}>1</span>
                                        Listing basics
                                    </span>
                                    <span className="text-muted">›</span>
                                    <span className={`d-inline-flex align-items-center gap-2 fw-semibold ${showListingDetails ? "text-primary" : "text-muted"}`}>
                                        <span className={`rounded-circle d-inline-flex align-items-center justify-content-center ${showListingDetails ? "bg-primary text-white" : "bg-light text-muted border"}`} style={{ width: 28, height: 28 }}>2</span>
                                        Item details
                                    </span>
                                </div>
                            </Card.Header>
                            <Card.Body>
                                <Row>
                                    <Col xs={12}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Main product photo <span className="text-danger">*</span></Form.Label>
                                            <div className="d-flex flex-column flex-md-row align-items-md-center gap-3">
                                                <div style={{ width: "150px", height: "150px", flex: "0 0 150px", border: "1px dashed #adb5bd", borderRadius: "10px", overflow: "hidden", background: "#f8f9fa" }}>
                                                    {mainImagePreview ? (
                                                        <img src={mainImagePreview} alt="Main product preview" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                                                    ) : (
                                                        <div className="d-flex h-100 flex-column align-items-center justify-content-center text-muted">
                                                            <span className="material-icons fs-2">add_a_photo</span>
                                                            <small>Add photo</small>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex-grow-1">
                                                    <Form.Control
                                                        type="file"
                                                        name="card_img"
                                                        accept="image/jpeg,image/png,image/webp"
                                                        onChange={handleMainImageChange}
                                                        required={!mainImage}
                                                    />
                                                    <Form.Text className="text-muted">JPG, PNG or WEBP. This is the main photo buyers see.</Form.Text>
                                                </div>
                                            </div>
                                        </Form.Group>
                                    </Col>
                                    <Col md={12}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Product Title <span className="text-danger">*</span></Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="title"
                                                placeholder="Enter a descriptive title for your product"
                                                value={formData.title}
                                                onChange={handleChange}
                                                maxLength={191}
                                                required
                                            />
                                        </Form.Group>
                                    </Col>

                                    {/* Parent Category Select */}
                                    <Col md={6}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Main Category <span className="text-danger">*</span></Form.Label>
                                            <Form.Select
                                                value={selectedParentCategory}
                                                onChange={handleParentCategoryChange}
                                                required
                                            >
                                                <option value="">Select Main Category</option>
                                                {categories.map((cat) => (
                                                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>

                                    {/* Child Category Select */}
                                    <Col md={6}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Sub Category <span className="text-danger">*</span></Form.Label>
                                            <Form.Select
                                                name="category_id"
                                                value={formData.category_id}
                                                onChange={handleChange}
                                                disabled={!subCategories.length}
                                                required
                                            >
                                                <option value="">
                                                    {subCategories.length ? "Select Sub Category" : "No Sub Categories Available"}
                                                </option>
                                                {subCategories.map((sub) => (
                                                    <option key={sub.id} value={sub.id}>{sub.name}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>

                                </Row>
                            </Card.Body>
                        </Card>

                        {initialListingDetailsComplete && (!categoryAttributesLoaded || preparingListingDetails) && (
                            <Card className="mb-4 shadow-sm border-0">
                                <Card.Body className="text-center py-5">
                                    <Spinner animation="border" variant="primary" className="mb-3" />
                                    <h5 className="mb-1">Preparing your listing</h5>
                                    <p className="text-muted mb-0">Finding item specifics and setting up the next steps...</p>
                                </Card.Body>
                            </Card>
                        )}

                        {showListingDetails && (
                            <>
                        {/* Category attributes */}
                        {formData.category_id && (
                            <Card className="mb-4 shadow-sm">
                                <Card.Header className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                                    <div>
                                        <h5 className="mb-1">Item specifics</h5>
                                        <small className="text-muted">Category suggestions are editable. Add your own specifics as needed.</small>
                                    </div>
                                    {!loadingAttributes && (
                                        <Button type="button" variant="primary" size="sm" onClick={addAttribute}>
                                            <FiPlus className="me-1" /> Add a specific
                                        </Button>
                                    )}
                                </Card.Header>
                                <Card.Body>
                                    {loadingAttributes ? (
                                        <div className="text-center py-4">
                                            <Spinner animation="border" variant="primary" className="mb-2" />
                                            <div className="fw-semibold">Finding item specifics for this category...</div>
                                            <small className="text-muted">This will only take a moment</small>
                                        </div>
                                    ) : attributeValues.length ? (
                                        <Row className="g-3">
                                            {attributeValues.map((attribute, index) => (
                                                <Col md={3} key={`attribute-${index}`}>
                                                    <div className="p-3 border rounded-3 h-100">
                                                        {attribute.isCustom ? (
                                                            <Form.Group className="mb-2">
                                                                <Form.Label>Specific name</Form.Label>
                                                                <Form.Control
                                                                    type="text"
                                                                    value={attribute.name}
                                                                    placeholder="e.g. Material"
                                                                    onChange={(event) => handleAttributeNameChange(index, event.target.value)}
                                                                />
                                                            </Form.Group>
                                                        ) : (
                                                            <Form.Label className="fw-semibold">{attribute.name}</Form.Label>
                                                        )}
                                                        <Form.Group className="position-relative">
                                                            <Form.Control
                                                                type="text"
                                                                autoComplete="off"
                                                                aria-label={`${attribute.name || "Custom"} value`}
                                                                placeholder="Enter or select value"
                                                                value={attribute.value}
                                                                onChange={(event) => handleAttributeChange(index, event.target.value)}
                                                                aria-expanded={activeAttributeSuggestion === index}
                                                                aria-controls={`attribute-suggestions-${index}`}
                                                                onFocus={() => setActiveAttributeSuggestion(index)}
                                                                onBlur={() => setActiveAttributeSuggestion(null)}
                                                            />
                                                            {activeAttributeSuggestion === index && attribute.suggestions?.length > 0 && (
                                                                <div
                                                                    id={`attribute-suggestions-${index}`}
                                                                    className="dropdown-menu show w-100 py-2 mt-1 overflow-auto"
                                                                    role="listbox"
                                                                    style={{ zIndex: 1050, maxHeight: 220 }}
                                                                >
                                                                    {attribute.suggestions.some((value) =>
                                                                        value.toLowerCase().includes(attribute.value.toLowerCase())
                                                                    ) ? attribute.suggestions
                                                                        .filter((value) => value.toLowerCase().includes(attribute.value.toLowerCase()))
                                                                        .map((value) => (
                                                                            <button
                                                                                key={value}
                                                                                type="button"
                                                                                className="dropdown-item px-3"
                                                                                role="option"
                                                                                aria-selected={value === attribute.value}
                                                                                style={{ paddingTop: 10, paddingBottom: 10 }}
                                                                                onMouseDown={(event) => event.preventDefault()}
                                                                                onClick={() => {
                                                                                    handleAttributeChange(index, value);
                                                                                    setActiveAttributeSuggestion(null);
                                                                                }}
                                                                            >
                                                                                {value}
                                                                            </button>
                                                                        )) : (
                                                                            <div className="px-3 py-2 text-muted small">
                                                                                No matching suggestions
                                                                            </div>
                                                                        )}
                                                                </div>
                                                            )}
                                                        </Form.Group>
                                                    </div>
                                                </Col>
                                            ))}
                                        </Row>
                                    ) : (
                                        <div className="text-center py-3">
                                            <p className="text-muted mb-3">No suggested specifics for this category. Add your own to help buyers find your item.</p>
                                            <Button type="button" variant="primary" onClick={addAttribute}>
                                                <FiPlus className="me-1" /> Add a specific
                                            </Button>
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        )}

                        {/* Remaining product details */}
                        <Card className="mb-4 shadow-sm">
                            <Card.Header><h5 className="mb-0">Product details</h5></Card.Header>
                            <Card.Body>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Brand <span className="text-danger">*</span></Form.Label>
                                            <Form.Select
                                                name="brand_id"
                                                value={formData.brand_id}
                                                onChange={handleChange}
                                                required
                                            >
                                                <option value="">Select Brand</option>
                                                {brands.map((b) => (
                                                    <option key={b.id} value={b.id}>{b.name}</option>
                                                ))}
                                            </Form.Select>
                                            <Button type="button" variant="link" className="px-0" onClick={() => setBrandModalOpen(true)}>
                                                Add a brand
                                            </Button>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Selling Style <span className="text-danger">*</span></Form.Label>
                                            <Form.Select name="selling_style" value={formData.selling_style} onChange={handleChange} required>
                                                <option value="Fixed">Fixed</option>
                                                <option value="Auction">Auction</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    {formData.selling_style === "Auction" && (
                                        <Col md={6}>
                                            <Form.Group className="mb-4">
                                                <Form.Label>Auction Minimum Price ($)</Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    name="auction_min_price"
                                                    placeholder="Enter minimum auction price"
                                                    value={formData.auction_min_price}
                                                    onChange={handleChange}
                                                />
                                            </Form.Group>
                                        </Col>
                                    )}

                                    {/* Condition Select */}
                                    <Col md={6}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Condition <span className="text-danger">*</span></Form.Label>
                                            <Form.Select name="product_condition" value={formData.product_condition} onChange={handleChange} required>
                                                <option value="new">New</option>
                                                <option value="used">Used</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Quantity</Form.Label>
                                            <Form.Control
                                                type="number"
                                                min="0"
                                                name="product_qty"
                                                placeholder="e.g. 50"
                                                value={formData.product_qty}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* Pricing & Costs */}
                        <Card className="mb-4 shadow-sm">
                            <Card.Header><h5 className="mb-0">Pricing & Cost Details</h5></Card.Header>
                            <Card.Body>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Sell Price Dollar ($) <span className="text-danger">*</span></Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                name="sell_price_dollar"
                                                placeholder="e.g. 25.50"
                                                value={formData.sell_price_dollar}
                                                onChange={handleChange}
                                                required
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Shipping Charges ($) <span className="text-danger">*</span></Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                name="shipping_charges_dollar"
                                                placeholder="e.g. 5.00"
                                                value={formData.shipping_charges_dollar}
                                                onChange={handleChange}
                                                required
                                            />
                                        </Form.Group>
                                    </Col>

                                    {/* <Col md={6}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Buying Price <span className="text-danger">*</span></Form.Label>
                                            <Form.Control
                                                type="number"
                                                min="0"
                                                name="buying_price"
                                                placeholder="e.g. 1000"
                                                value={formData.buying_price}
                                                onChange={handleChange}
                                                required
                                            />
                                        </Form.Group>
                                    </Col> */}

                                    {/* <Col md={6}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Short Buying Price <span className="text-danger">*</span></Form.Label>
                                            <Form.Control
                                                type="number"
                                                min="0"
                                                name="short_buying_price"
                                                placeholder="e.g. 950"
                                                value={formData.short_buying_price}
                                                onChange={handleChange}
                                                required
                                            />
                                        </Form.Group>
                                    </Col> */}
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* Descriptions */}
                        <Card className="mb-4 shadow-sm">
                            <Card.Header><h5 className="mb-0">Short description</h5></Card.Header>
                            <Card.Body>
                                <SunEditor
                                    setContents={formData.short_description}
                                    onChange={handleShortDescriptionChange}
                                    setOptions={{ buttonList: editorButtonList, height: 180, placeholder: "Add a short summary of your product..." }}
                                />
                            </Card.Body>
                        </Card>

                        <Card className="mb-4 shadow-sm">
                            <Card.Header><h5 className="mb-0">Description</h5></Card.Header>
                            <Card.Body>
                                <SunEditor
                                    setContents={formData.description}
                                    onChange={handleDescriptionChange}
                                    setOptions={{ buttonList: editorButtonList, height: 300, placeholder: "Describe condition, features and other important details..." }}
                                />
                            </Card.Body>
                        </Card>

                        {/* Gallery Images - added at the end of the listing */}
                        <Card className="mb-4 shadow-sm">
                            <Card.Header>
                                <h5 className="mb-1">Add more photos</h5>
                                <small className="text-muted">Add extra angles or details to complete your listing.</small>
                            </Card.Header>
                            <Card.Body>
                                <Row className="g-3">
                                    {galleryPreviews.map((src, idx) => (
                                        <Col md={3} sm={4} key={idx}>
                                            <div className="position-relative" style={{ height: "140px", border: "1px solid #ddd", borderRadius: "6px", overflow: "hidden" }}>
                                                <img src={src} alt={`Gallery ${idx}`} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                                <Button
                                                    variant="danger"
                                                    size="sm"
                                                    className="position-absolute top-0 end-0 m-1 p-1 rounded-circle"
                                                    onClick={() => removeGalleryImage(idx)}
                                                >
                                                    <FiX size={14} />
                                                </Button>
                                            </div>
                                        </Col>
                                    ))}
                                    <Col md={3} sm={4}>
                                        <label
                                            className="d-flex flex-column align-items-center justify-content-center border border-primary border-dashed rounded text-primary"
                                            style={{ height: "140px", cursor: "pointer", backgroundColor: "#f8f9fa" }}
                                        >
                                            <FiPlus size={28} />
                                            <span className="fw-bold mt-1">Add Images</span>
                                            <input type="file" hidden multiple accept="image/jpg,image/jpeg,image/png,image/webp" onChange={handleGalleryImagesChange} />
                                        </label>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        <div className="d-flex justify-content-end gap-2 mb-5">
                            <Button variant="secondary" onClick={() => navigate(-1)} disabled={saving}>Cancel</Button>
                            <Button type="submit" variant="primary" disabled={saving}>
                                {saving ? <Spinner size="sm" animation="border" /> : "Save Fresh Product"}
                            </Button>
                        </div>
                            </>
                        )}
                    </Form>
                </Col>
            </Row>
            <Modal show={brandModalOpen} onHide={() => setBrandModalOpen(false)} centered>
                <Form onSubmit={handleAddBrand}>
                    <Modal.Header closeButton><Modal.Title>Add brand</Modal.Title></Modal.Header>
                    <Modal.Body>
                        <Form.Label>Brand name</Form.Label>
                        <Form.Control autoFocus required value={brandName} onChange={(event) => setBrandName(event.target.value)} />
                    </Modal.Body>
                    <Modal.Footer>
                        <Button type="button" variant="secondary" onClick={() => setBrandModalOpen(false)}>Cancel</Button>
                        <Button type="submit" disabled={addingBrand}>{addingBrand ? "Adding..." : "Add brand"}</Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </PageLayout>
    );
}